import type { PinContentFn } from './PinContent';
import type { MenuItemChildProps } from './SectionMenuItemChild';
import type { SectionUserProfileProps } from './SectionUserProfile';

import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { EMPTY_ARRAY } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';

import { SectionLogo } from './SectionLogo';
import { SectionMenuItem } from './SectionMenuItem';
import { SectionUpgrade } from './SectionUpgrade';
import { SectionUserProfile } from './SectionUserProfile';

const sidebarDefaultPropLogoConfig = {
  height: 64,
  marginBottom: 46,
  marginTop: 0,
};

const sidebarDefaultPropUpgradeConfig = {
  height: 32,
  marginBottom: 20,
  marginTop: 0,
};

const sidebarDefaultPropMenuConfig = {
  selected: undefined,
  isListOpenSingle: true,
  selectedSeparator: '/',
};

const sidebarDefaultPropUserConfig = {
  avatar: undefined,
  name: '',
  email: '',
  isModalOnClose: false,
  isModalOnCloseOutside: false,
  hasEventOnCloseOutside: false,
  menuItem: [],
  height: 64,
};

interface SidebarProps {
  /**
   * Sidebar width on open
   */
  sizeOpen?: any;
  /**
   * Sidebar width on close
   */
  sizeClose?: any;
  /**
   * Sidebar height
   */
  height?: any;
  /**
   * If sidebar should open/close
   */
  isOpen?: boolean;
  /*
   * Handle sidebar open/close
   */
  handleSidebar?: (isOpen: boolean) => void;
  /**
   * Additional Container Classes for sidebar
   */
  additionalContainerClasses?: string;
  /**
   * Logo source path
   */
  logo?: any;
  /**
   * Logo link
   */
  logoLink?: string;
  /**
   * Logo configuration from sidebar
   */
  logoConfig?: {
    height: number;
    marginBottom: number;
    marginTop: number;
  };
  /**
   * Logo additional Container Classes
   */
  additionalLogoContainerClasses?: string;
  /**
   * Logo configuration from sidebar
   */
  upgradeConfig?: {
    height: number;
    marginBottom: number;
    marginTop: number;
  };
  /**
   * Logo additional image Classes
   */
  additionalUpgradeClasses?: string;
  /**
   * Menu item list
   */
  menuItems?: MenuItemChildProps[];
  /**
   *  Menu config from sidebar
   */
  menuConfig?: {
    selected?: any;
    openItem?: string;
    isListOpenSingle?: boolean;
    selectedSeparator?: string;
  };
  /**
   * Menu additional Menu Item Classes
   */
  additionalMenuItemClasses?: string;
  /**
   * Menu additional Menu Item Child Classes
   */
  additionalMenuItemChildClasses?: string;
  /**
   * Menu additional Menu Item Icon Classes
   */
  additionalMenuItemIconClasses?: string;
  /**
   * User config from sidebar
   */
  userConfig?: SectionUserProfileProps;
  /**
   * Additional User Container Classes
   */
  showUpgrade?: boolean;
  /**
   * Pin content callback
   */
  pinContentCallback?: PinContentFn;
  /**
   * User additional User Container Classes
   */
  additionalUserContainerClasses?: string;
  /**
   * User additional Username Classes
   */
  additionalUserNameClasses?: string;
  /**
   * User additional User Email Classes
   */
  additionalUserEmailClasses?: string;
  /**
   * User additional User Menu Container Classes
   */
  additionalUserMenuContainerClasses?: string;
  /**
   * User additional User Menu Classes
   */
  additionalUserMenuClasses?: string;
  /**
   * Component test id
   */
  testId?: string;
}

const Sidebar = ({
  sizeOpen = 240,
  sizeClose = 68,
  isOpen = true,
  additionalContainerClasses = '',
  handleSidebar,
  logo,
  logoLink,
  logoConfig = sidebarDefaultPropLogoConfig,
  additionalLogoContainerClasses = '',
  showUpgrade,
  upgradeConfig = sidebarDefaultPropUpgradeConfig,
  additionalUpgradeClasses = '',
  menuItems = EMPTY_ARRAY,
  menuConfig = sidebarDefaultPropMenuConfig,
  additionalMenuItemClasses = '',
  additionalMenuItemChildClasses = '',
  additionalMenuItemIconClasses = '',
  userConfig = sidebarDefaultPropUserConfig,
  additionalUserContainerClasses = '',
  additionalUserNameClasses = '',
  additionalUserEmailClasses = '',
  additionalUserMenuContainerClasses = '',
  additionalUserMenuClasses = '',
  pinContentCallback,
  testId,
  ...props
}: SidebarProps) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(isOpen);
  const validHeight =
    (logo
      ? logoConfig.height + logoConfig.marginBottom + logoConfig.marginTop
      : 0) +
    (userConfig?.height || 0) +
    (showUpgrade && upgradeConfig
      ? upgradeConfig.height +
        upgradeConfig.marginBottom +
        upgradeConfig.marginTop
      : 0);

  useEffect(() => {
    setIsSidebarOpen(isOpen);
  }, [isOpen]);

  const handleSidebarOpen = () => {
    setIsSidebarOpen(!isSidebarOpen);
    handleSidebar?.(!isSidebarOpen);
  };

  return (
    <>
      <button
        type="button"
        className={twMerge(
          'lg:hidden absolute top-0 left-0 bg-slate-600 w-full h-full z-20 transition',
          'duration-500 ease-in-out cursor-pointer',
          isOpen ? 'opacity-50' : 'opacity-0 pointer-events-none',
        )}
        onClick={handleSidebarOpen}
      />
      <div
        className={twMerge(
          'transition-all bg-white dark:bg-slate-950',
          'ease-in-out',
          'duration-normal',
          'border-r',
          'border-slate-100 dark:border-slate-800',
          !isSidebarOpen
            ? '-translate-x-full sm:translate-x-0'
            : 'translate-x-0',
          additionalContainerClasses,
        )}
        style={{
          width: isSidebarOpen ? sizeOpen : sizeClose,
          bottom: 0,
          top: 0,
        }}
        {...props}
        {...getTestProps(testId)}
      >
        {/* Section: Logo */}
        {logo && (
          <SectionLogo
            logo={logo}
            logoLink={logoLink}
            small={!isSidebarOpen}
            height={logoConfig.height}
            marginBottom={logoConfig.marginBottom}
            marginTop={logoConfig.marginTop}
            additionalLogoContainerClasses={additionalLogoContainerClasses}
            {...getTestProps(testId, 'logo', 'testId')}
          />
        )}
        {/* Section upgrade plan */}
        {showUpgrade && (
          <SectionUpgrade
            height={upgradeConfig?.height}
            marginBottom={upgradeConfig?.marginBottom}
            marginTop={upgradeConfig?.marginTop}
            additionalUpgradeContainerClasses={additionalUpgradeClasses}
            isOpen={isSidebarOpen}
          />
        )}

        {/* Section: Menu Item */}
        <div
          style={{
            height: `calc(100% - ${validHeight}px)`,
          }}
          className={twMerge(
            'overflow-x-hidden overflow-y-overlay scrollbar-sm',
          )}
        >
          <SectionMenuItem
            menuItems={menuItems}
            pinContentCallback={pinContentCallback}
            selected={menuConfig.selected}
            selectedSeparator={menuConfig.selectedSeparator}
            isListOpenSingle={menuConfig.isListOpenSingle}
            isOpen={isSidebarOpen}
            openItem={menuConfig.openItem}
            handleParentOpen={handleSidebarOpen}
            additionalMenuItemClasses={additionalMenuItemClasses}
            additionalMenuItemIconClasses={additionalMenuItemIconClasses}
            additionalMenuItemChildClasses={additionalMenuItemChildClasses}
            {...getTestProps(testId, 'menu-item', 'testId')}
          />
        </div>

        <SectionUserProfile
          menuItem={userConfig.menuItem}
          avatar={userConfig.avatar}
          name={userConfig.name}
          email={userConfig.email}
          height={userConfig.height}
          isModalOnClose={userConfig.isModalOnClose}
          isModalOnCloseOutside={userConfig.isModalOnCloseOutside}
          onClick={handleSidebarOpen}
          isOpen={isSidebarOpen}
          handleSidebarOpen={handleSidebarOpen}
          sizeClose={sizeClose}
          additionalUserContainerClasses={additionalUserContainerClasses}
          additionalUserNameClasses={additionalUserNameClasses}
          additionalUserEmailClasses={additionalUserEmailClasses}
          additionalUserMenuContainerClasses={
            additionalUserMenuContainerClasses
          }
          additionalUserMenuClasses={additionalUserMenuClasses}
          hasEventOnCloseOutside={userConfig.hasEventOnCloseOutside}
          {...getTestProps(testId, 'user-profile', 'testId')}
        />
      </div>
    </>
  );
};

export default Sidebar;
